import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyLikertColumnSx,
  surveyLikertHorizontalRowSx,
  surveyLikertLabelSx,
  surveyLikertRadioButtonSx,
  surveyLikertRadioInnerDotSx,
  surveyLikertRadioSelectedSx,
  surveyLikertRadioUnselectedSx,
  surveyLikertRootSx,
  surveyLikertVerticalLabelSx,
  surveyLikertVerticalRowSx,
  surveyLikertVerticalStackSx,
} from '../../../styles/surveyExecutionStyles'

interface LikertQuestionProps {
  readonly question: SurveyQuestion
  /** Selected scale point (1-based). */
  readonly value: number | null
  readonly onChange: (point: number) => void
}

function LikertRadioControl({ selected }: { readonly selected: boolean }) {
  if (selected) {
    return (
      <Box sx={surveyLikertRadioSelectedSx} aria-hidden>
        <Box sx={surveyLikertRadioInnerDotSx} />
      </Box>
    )
  }
  return <Box sx={surveyLikertRadioUnselectedSx} aria-hidden />
}

export function LikertQuestion({ question, value, onChange }: LikertQuestionProps) {
  const labels = question.likertLabels
  const horizontal = question.likertLayoutHorizontal

  if (horizontal) {
    return (
      <Box sx={surveyLikertRootSx} role="radiogroup" aria-label={question.prompt}>
        <Box sx={surveyLikertHorizontalRowSx} data-testid={`survey-q-${question.id}-likert-row`}>
          {labels.map((label, index) => {
            const point = index + 1
            const selected = value === point
            return (
              <Box key={point} sx={surveyLikertColumnSx}>
                <Box
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={label}
                  data-testid={`survey-q-${question.id}-likert-${point}`}
                  onClick={() => onChange(point)}
                  sx={surveyLikertRadioButtonSx}
                >
                  <LikertRadioControl selected={selected} />
                </Box>
                <Typography component="span" sx={surveyLikertLabelSx}>
                  {label}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={surveyLikertRootSx} role="radiogroup" aria-label={question.prompt}>
      <Box sx={surveyLikertVerticalStackSx} data-testid={`survey-q-${question.id}-likert-stack`}>
        {labels.map((label, index) => {
          const point = index + 1
          const selected = value === point
          return (
            <Box key={point} sx={surveyLikertVerticalRowSx}>
              <Box
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={label}
                data-testid={`survey-q-${question.id}-likert-${point}`}
                onClick={() => onChange(point)}
                sx={surveyLikertRadioButtonSx}
              >
                <LikertRadioControl selected={selected} />
              </Box>
              <Typography component="span" sx={surveyLikertVerticalLabelSx}>
                {label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
